import * as flash from './flash';
import {
  getCartErrorMessage,
  hasAddressCartErrors,
  hasCartErrors,
} from './cart-errors';
import { activateButtonLoading, deactivateButtonLoading } from './btn-loading';
import { pushAddToCartFromResponse } from './ga4-ecommerce';

(function () {
  const form = document.querySelector('[data-product-purchase-form]');
  let purchasable = null;
  let qtyInput = null;
  let addToCartButton = null;
  let submitContainer = null;
  let isUpdatingGalleryFromVariant = false;
  if (form) {
    purchasable = form.querySelector('input[name="purchasableId"]');
    qtyInput = form.querySelector('input[name="qty"]');
    addToCartButton = form.querySelector('button[type="submit"]');
    submitContainer = form.querySelector(
      '[data-product-purchase-submit-container]'
    );
  }

  // Build a list of variants with their option values.
  // IMPORTANT: update these keys to match your variant field/option handles.
  const VARIANTS = window.variants;
  const MODIFIERS = [
    { name: 'opt_size', key: 'size' },
    { name: 'opt_colour', key: 'colour' },
    { name: 'opt_configuration', key: 'configuration' },
    { name: 'opt_gauge', key: 'gauge' },
    { name: 'opt_grouping', key: 'grouping' },
    { name: 'opt_machineType', key: 'machineType' },
    { name: 'opt_fitSize', key: 'fitSize' },
    { name: 'opt_variantOption', key: 'variantOption' },
    { name: 'opt_packSize', key: 'packSize' },
    { name: 'opt_packUnit', key: 'packUnit' },
  ];
  const FLEX_MODIFIER_KEYS = ['configuration', 'gauge', 'grouping'];
  const PRIMARY_FLEX_MODIFIER_KEYS = ['configuration', 'gauge'];

  function setPurchaseControlsDisabled(disabled, options = {}) {
    const { outOfStock = false } = options;
    if (!form) return;
    if (qtyInput) {
      qtyInput.disabled = disabled;
      const qtyContainer = qtyInput.closest('[data-qty-input]');
      if (disabled) {
        qtyContainer
          ?.querySelectorAll(
            '[data-qty-input-decrease], [data-qty-input-increase]'
          )
          .forEach((btn) => {
            btn.disabled = true;
            btn.setAttribute('aria-disabled', 'true');
          });
      } else {
        // Re-sync +- button disabled state from current max/value
        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }
    if (addToCartButton) {
      addToCartButton.disabled = disabled;
      let addToCartButtonText = 'Add to cart';
      if (disabled && outOfStock) {
        addToCartButtonText = 'Out of stock';
      }
      addToCartButton.textContent = addToCartButtonText;
    }
    if (submitContainer)
      submitContainer.classList.toggle('modifier--disabled', disabled);
  }

  function isVariantDisabled(match) {
    if (!match) return true;

    // Only treat explicitly disabled variants as unselectable.
    // Stock and purchasability are handled separately so users can still
    // choose radio-card--out-of-stock combinations to view their details.
    if ('disabled' in match) return Boolean(match.disabled);
    if ('isDisabled' in match) return Boolean(match.isDisabled);

    return false;
  }

  function isVariantOutOfStock(match) {
    if (!match) return false;
    if ('stock' in match && Number(match.stock) <= 0) return true;
    return false;
  }

  function resolveQtyMax(match) {
    if (!match) return null;

    const variantMaxQty = Number(match.maxQty);
    const hasVariantMax = Number.isFinite(variantMaxQty) && variantMaxQty > 0;

    const inventoryTracked = match.inventoryTracked !== false;
    const hasUnlimitedStock = Boolean(match.hasUnlimitedStock);
    const stock = Number(match.stock);
    const hasStockMax =
      inventoryTracked &&
      !hasUnlimitedStock &&
      Number.isFinite(stock) &&
      stock >= 1;

    if (hasVariantMax && hasStockMax) {
      return Math.min(Math.floor(variantMaxQty), Math.floor(stock));
    }
    if (hasVariantMax) return Math.floor(variantMaxQty);
    if (hasStockMax) return Math.floor(stock);
    return null;
  }

  function updateQtyMax(match) {
    if (!qtyInput) return;

    const maxQty = resolveQtyMax(match);
    if (maxQty == null) {
      qtyInput.removeAttribute('max');
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }

    qtyInput.setAttribute('max', String(maxQty));

    const currentQty = parseInt(qtyInput.value, 10) || 1;
    if (currentQty > maxQty) qtyInput.value = maxQty;

    qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
    if (currentQty > maxQty) {
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function readSelected(name) {
    const checkedRadio = form.querySelector(
      `input[type="radio"][name="${name}"]:checked`
    );
    if (checkedRadio) return checkedRadio.value;

    const select = form.querySelector(`select[name="${name}"]`);
    if (select) return select.value || null;

    const hidden = form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (hidden) return hidden.value || null;

    return null;
  }

  function getChosenValues() {
    return MODIFIERS.reduce((acc, modifier) => {
      acc[modifier.key] = readSelected(modifier.name);
      return acc;
    }, {});
  }

  function variantMatchesChosen(variant, chosen, skipKeys = []) {
    const skipSet = new Set(Array.isArray(skipKeys) ? skipKeys : [skipKeys]);
    return MODIFIERS.every(({ key }) => {
      if (skipSet.has(key)) return true;
      if (chosen[key] == null || chosen[key] === '') return true;
      return variant[key] === chosen[key];
    });
  }

  function getOptionSkipKeys(modifierKey) {
    const skipKeys = [modifierKey];
    const modifierIndex = MODIFIERS.findIndex(({ key }) => key === modifierKey);
    if (modifierIndex !== -1) {
      MODIFIERS.slice(modifierIndex + 1).forEach(({ key }) => {
        if (!skipKeys.includes(key)) skipKeys.push(key);
      });
    }
    if (PRIMARY_FLEX_MODIFIER_KEYS.includes(modifierKey)) {
      FLEX_MODIFIER_KEYS.forEach((key) => {
        if (!skipKeys.includes(key)) skipKeys.push(key);
      });
    }
    return skipKeys;
  }

  function getOptionState(modifierKey, value, chosen) {
    const skipKeys = getOptionSkipKeys(modifierKey);
    const matchingVariants = VARIANTS.filter(
      (variant) =>
        variant[modifierKey] === value &&
        variantMatchesChosen(variant, chosen, skipKeys)
    );
    const enabledVariants = matchingVariants.filter(
      (variant) => !isVariantDisabled(variant)
    );

    const isCompatible = enabledVariants.length > 0;
    const isOutOfStock =
      isCompatible &&
      enabledVariants.every((variant) => isVariantOutOfStock(variant));

    return { isCompatible, isOutOfStock };
  }

  function canUseOption(modifierKey, value, chosen) {
    return getOptionState(modifierKey, value, chosen).isCompatible;
  }

  function countMatchingChosenValues(variant, chosen) {
    return MODIFIERS.reduce((count, { key }) => {
      if (chosen[key] == null || chosen[key] === '') return count;
      return variant[key] === chosen[key] ? count + 1 : count;
    }, 0);
  }

  function getVariantForGalleryImage(imageId) {
    if (!imageId) return null;

    const imageIdString = String(imageId);
    const variants = VARIANTS.filter(
      (variant) =>
        variant.imageId != null &&
        String(variant.imageId) === imageIdString &&
        !isVariantDisabled(variant)
    );

    if (variants.length === 0) return null;

    const chosen = getChosenValues();
    const exactMatch = variants.find((variant) =>
      variantMatchesChosen(variant, chosen)
    );
    if (exactMatch) return exactMatch;

    return variants
      .slice()
      .sort(
        (a, b) =>
          countMatchingChosenValues(b, chosen) -
          countMatchingChosenValues(a, chosen)
      )[0];
  }

  function setSelectedValue(name, value) {
    const radios = Array.from(
      form.querySelectorAll(`input[type="radio"][name="${name}"]`)
    );
    if (radios.length > 0) {
      const target = radios.find((radio) => radio.value === value);
      if (target) target.checked = true;
      return;
    }

    const select = form.querySelector(`select[name="${name}"]`);
    if (select && value != null) {
      const option = Array.from(select.options).find(
        (opt) => opt.value === value
      );
      if (option) select.value = value;
    }
  }

  function resolveChosenValues(chosen, changedKey = null) {
    if (!changedKey || !FLEX_MODIFIER_KEYS.includes(changedKey)) return chosen;

    const exactMatch = VARIANTS.find((variant) =>
      MODIFIERS.every(({ key }) => {
        if (chosen[key] == null || chosen[key] === '') return true;
        return variant[key] === chosen[key];
      })
    );
    if (exactMatch) return chosen;

    const lockedKeys = MODIFIERS.map(({ key }) => key).filter(
      (key) => !FLEX_MODIFIER_KEYS.includes(key)
    );
    lockedKeys.push(changedKey);

    const preferredMatch = VARIANTS.find(
      (variant) =>
        !isVariantDisabled(variant) &&
        lockedKeys.every((key) => {
          if (chosen[key] == null || chosen[key] === '') return true;
          return variant[key] === chosen[key];
        })
    );

    if (!preferredMatch) return chosen;

    FLEX_MODIFIER_KEYS.forEach((key) => {
      const modifier = MODIFIERS.find((item) => item.key === key);
      if (!modifier) return;
      setSelectedValue(modifier.name, preferredMatch[key]);
    });

    return getChosenValues();
  }

  // function isOptionUsedByAnyVariant(modifierKey, value) {
  //   return VARIANTS.some(
  //     (variant) =>
  //       variant[modifierKey] === value && !isVariantDisabled(variant)
  //   );
  // }

  function syncOptionAvailability(chosen) {
    MODIFIERS.forEach(({ name, key }) => {
      const radios = Array.from(
        form.querySelectorAll(`input[type="radio"][name="${name}"]`)
      );
      if (radios.length > 0) {
        radios.forEach((radio) => {
          const { isCompatible, isOutOfStock } = getOptionState(
            key,
            radio.value,
            chosen
          );
          // const existsOnAnyVariant = isOptionUsedByAnyVariant(key, radio.value);
          radio.disabled = !isCompatible;
          radio.classList.toggle('radio-card--out-of-stock', isOutOfStock);
          const radioCard = radio.closest('.radio-card');
          if (radioCard) {
            radioCard.classList.toggle(
              'radio-card--out-of-stock',
              isOutOfStock
            );
            if (!isCompatible) {
              radioCard.style.display = 'none';
            } else {
              radioCard.style.display = '';
            }
          }
        });

        const checked = form.querySelector(
          `input[type="radio"][name="${name}"]:checked`
        );
        if (checked && checked.disabled) {
          const firstEnabled = radios.find((radio) => !radio.disabled);
          if (firstEnabled) firstEnabled.checked = true;
        }
      }

      const select = form.querySelector(`select[name="${name}"]`);
      if (select) {
        const options = Array.from(select.options);
        options.forEach((option) => {
          if (!option.value) return;
          const { isCompatible, isOutOfStock } = getOptionState(
            key,
            option.value,
            chosen
          );
          // const existsOnAnyVariant = isOptionUsedByAnyVariant(key, option.value);
          option.disabled = !isCompatible;
          option.classList.toggle('radio-card--out-of-stock', isOutOfStock);
        });

        const selected = options.find((option) => option.selected);
        if (selected && selected.hidden) {
          const firstVisible = options.find(
            (option) => option.value && !option.hidden
          );
          select.value = firstVisible ? firstVisible.value : '';
        }
      }
    });
  }

  function updatePurchasableId(changedKey = null) {
    let chosen = getChosenValues();
    chosen = resolveChosenValues(chosen, changedKey);
    syncOptionAvailability(chosen);
    chosen = getChosenValues();

    const match = VARIANTS.find((v) => variantMatchesChosen(v, chosen));

    const variantOutOfStock = isVariantOutOfStock(match);
    const missingVariantSelection = !match;

    if (match) {
      purchasable.value = match.id;
      // Set max before re-enabling controls so +- buttons pick up the limit
      updateQtyMax(match);
    } else {
      purchasable.value = '';
      updateQtyMax(null);
    }

    setPurchaseControlsDisabled(variantOutOfStock || missingVariantSelection, {
      outOfStock: variantOutOfStock,
    });

    if (match) {
      document
        .querySelectorAll('[data-product-purchase-price]')
        .forEach((el) => {
          el.innerHTML = match.displayPrice;
        });

      document.querySelectorAll('[data-product-purchase-sku]').forEach((el) => {
        el.innerHTML = match.sku;
      });

      document
        .querySelectorAll('[data-product-purchase-pack-size]')
        .forEach((el) => {
          const packDisplay = (match.packSize || '').trim();
          el.innerHTML = packDisplay;

          // Toggle trailing "PACK" when the value is already a unit term.
          const parent = el.parentElement;
          if (!parent) return;
          const isUnitTerm = /\b(box|pack|bottle|item|unit|carton)s?\b/i.test(
            packDisplay
          );
          const suffix = !packDisplay || isUnitTerm ? '' : ' PACK';
          // Keep the inner pack-size span; replace only the trailing suffix text.
          parent.replaceChildren(
            el,
            ...(suffix ? [document.createTextNode(suffix)] : [])
          );
        });

      document
        .querySelectorAll('[data-product-purchase-expiry]')
        .forEach((el) => {
          const expiryDate = (match.expiryDate || '').trim();
          if (!expiryDate) {
            el.innerHTML = '';
            el.style.display = 'none';
            return;
          }
          el.innerHTML = `Expires <span>${expiryDate}</span>`;
          el.style.display = '';
        });

      document
        .querySelectorAll('[data-product-purchase-stock-message]')
        .forEach((el) => {
          el.innerHTML = match.stockMessage;
          if (!match.stockMessage || match.stockMessage.trim() === '') {
            el.style.display = 'none';
          } else {
            el.style.display = '';
          }
        });

      document.querySelectorAll('[data-product-variant-desc]').forEach((el) => {
        el.innerHTML = match.description;
        if (!match.description || match.description.trim() === '') {
          el.style.display = 'none';
        } else {
          el.style.display = '';
        }
      });

      updateGalleryImage(match.imageId);
    }
  }

  function updateGalleryImage(imageId) {
    if (!imageId) return;

    const gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;

    const imageIdString = String(imageId);
    const thumb = gallery.querySelector(
      `[data-product-gallery-thumb-index][data-product-gallery-image-id="${imageIdString}"]`
    );

    if (thumb) {
      isUpdatingGalleryFromVariant = true;
      try {
        thumb.click();
      } finally {
        isUpdatingGalleryFromVariant = false;
      }
    }
  }

  function selectVariantFromGalleryImage(imageId) {
    if (!form || isUpdatingGalleryFromVariant) return;

    const match = getVariantForGalleryImage(imageId);
    if (!match) return;

    MODIFIERS.forEach(({ name, key }) => {
      if (match[key] == null || match[key] === '') return;
      setSelectedValue(name, match[key]);
    });

    updatePurchasableId();
  }

  function initForm() {
    if (!form) return;

    form.addEventListener('change', (e) => {
      if (
        e.target.matches('input[type="radio"]') ||
        e.target.matches('select')
      ) {
        const changedKey = MODIFIERS.find(
          ({ name }) => e.target.name === name
        )?.key;
        updatePurchasableId(changedKey || null);
      }
    });

    // set initial
    updatePurchasableId();

    // form submit event
    form.addEventListener('submit', function (e) {
      const id = e.target.querySelector('input[name="purchasableId"]').value;
      const qty = e.target.querySelector('input[name="qty"]').value;
      addToCart(id, qty, addToCartButton);
      e.preventDefault();
      return false;
    });
  }

  initForm();

  document.addEventListener('product-gallery:change', (e) => {
    selectVariantFromGalleryImage(e.detail?.imageId);
  });

  function updateCartQuantity(cart) {
    const totalQty = Number(cart?.totalQty);
    if (!Number.isFinite(totalQty)) return;

    document.querySelectorAll('[data-cart-quantity]').forEach((el) => {
      el.textContent =
        totalQty > 0 ? totalQty : el.dataset.cartQuantityEmptyText || '0';
    });

    document.querySelectorAll('[data-cart-quantity-badge]').forEach((el) => {
      el.hidden = totalQty <= 0;
    });
  }

  async function addToCart(purchasableId, qty = 1, loadingEl = null) {
    const id = Number(purchasableId);
    const quantity = Number(qty);
    if (!Number.isInteger(id) || id <= 0) {
      flash.create('This product can’t be added to the cart right now.', {
        type: 'error',
        hidePrevious: true,
      });
      return;
    }

    if (loadingEl) {
      activateButtonLoading(loadingEl);
    }

    try {
      const response = await fetch('/actions/commerce/cart/update-cart/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': window.csrfTokenValue,
        },
        body: JSON.stringify({
          purchasableId: id,
          qty: Number.isInteger(quantity) && quantity > 0 ? quantity : 1,
          clearNotices: 1,
        }),
      });

      const data = await response.json();

      if (!response.ok || hasCartErrors(data)) {
        const message = getCartErrorMessage(data);
        const options = { type: 'error', hidePrevious: true };
        if (hasAddressCartErrors(data)) {
          options.action = {
            text: 'Fix Address',
            href: '/account/addresses/',
            icon: 'pin-edit',
          };
        }
        console.error('Cart error:', data);
        flash.create(message, options);
        return;
      }

      let message = data.message;
      if (data.cart.notices.length > 0) {
        message = data.cart.notices[data.cart.notices.length - 1].message;
      }
      updateCartQuantity(data.cart);
      flash.create(message, { hidePrevious: true });
      if (window.protatDoTracking) {
        pushAddToCartFromResponse(data, purchasableId, Number(qty) || 1);
      }
      return data;
    } catch (error) {
      console.error('Network error:', error);
      flash.create(
        'Something went wrong adding this item to your cart. Please try again.',
        {
          type: 'error',
          hidePrevious: true,
        }
      );
    } finally {
      if (loadingEl) {
        deactivateButtonLoading(loadingEl);
      }
    }
  }

  // product card add to cart button click event
  document.addEventListener('click', function (e) {
    const button = e.target.closest('[data-product-purchase-add-to-cart]');
    if (!button) return;

    const variantId = button.dataset.variantId;
    addToCart(variantId, 1, button);
    e.preventDefault();
  });
})();
